/**
 * Resources Page
 *
 * Displays FAQs, helpful guides, and additional resources for CPR training.
 * Includes tabbed FAQ interface organized by category.
 */

import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { FAQAccordion } from '../../components/cpr/FAQAccordion';
import { Button } from '../../components/Button';
import { faqs, getCategoryTitle } from '../../data/faqs';
import type { FAQCategory } from '../../types/faq';

// Tab configuration
const FAQ_TABS: Array<{ id: FAQCategory; label: string }> = [
  { id: 'general', label: 'General' },
  { id: 'booking-payment', label: 'Booking & Payment' },
  { id: 'certification-renewals', label: 'Certification & Renewals' },
  { id: 'group-training', label: 'Group Training' },
];

export default function Resources() {
  // STATE: Active tab
  const [activeTab, setActiveTab] = useState<FAQCategory>('general');
  const navigate = useNavigate();

  // Generate FAQPage Schema for structured data
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>CPR Training FAQs | Common Questions Answered</title>
        <meta name="title" content="CPR Training FAQs | Common Questions Answered" />
        <meta
          name="description"
          content="Find answers to frequently asked questions about CPR certification, booking, renewals, and group training. Expert answers from certified instructors."
        />
        <meta
          name="keywords"
          content="CPR FAQ, certification questions, booking help, renewal information, group training FAQ"
        />
        <meta name="author" content="CPR Training Direct" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cprtrainingdirect.com/resources" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cprtrainingdirect.com/resources" />
        <meta property="og:title" content="CPR Training FAQs | Common Questions Answered" />
        <meta
          property="og:description"
          content="Find answers to frequently asked questions about CPR certification, booking, renewals, and group training. Expert answers from certified instructors."
        />
        <meta property="og:image" content="https://cprtrainingdirect.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="CPR Training Direct" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cprtrainingdirect.com/resources" />
        <meta name="twitter:title" content="CPR Training FAQs | Common Questions Answered" />
        <meta
          name="twitter:description"
          content="Find answers to frequently asked questions about CPR certification, booking, renewals, and group training. Expert answers from certified instructors."
        />
        <meta name="twitter:image" content="https://cprtrainingdirect.com/og-image.jpg" />

        {/* Geo Tags for Local SEO */}
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content="Orlando" />
        <meta name="geo.position" content="28.5383;-81.3792" />
        <meta name="ICBM" content="28.5383, -81.3792" />

        {/* Schema.org Structured Data - FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* Hero Section - Brutalist */}
      <section className="bg-indigo_dye-500 text-white py-16 md:py-24 px-6">
        <div className="container mx-auto text-center">
          <h1
            data-aos="fade-up"
            className="font-display text-6xl md:text-7xl lg:text-8xl font-bold uppercase tracking-wider mb-6"
          >
            Resources & FAQs
          </h1>
          <p
            data-aos="fade-up"
            data-aos-delay="100"
            className="font-body text-xl md:text-2xl text-indigo_dye-100 leading-relaxed max-w-3xl mx-auto"
          >
            Find answers to your questions about CPR certification, booking, renewals, and more
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2
              data-aos="fade-up"
              className="font-display text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-wider text-center mb-12 text-indigo_dye-500"
            >
              Frequently Asked Questions
            </h2>

            {/* Tab Navigation - Brutalist Buttons */}
            <div data-aos="fade-up" data-aos-delay="100" className="flex flex-wrap justify-center gap-4 mb-12">
              {FAQ_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 font-mono font-bold uppercase tracking-wide border-4 transition-all ${
                    activeTab === tab.id
                      ? 'bg-indigo_dye-600 text-white border-indigo_dye-600 shadow-brutalist'
                      : 'bg-white text-gray-700 border-gray-400 hover:border-indigo_dye-500 hover:shadow-brutalist-sm'
                  }`}
                  aria-pressed={activeTab === tab.id}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* FAQ Accordion - Brutalist Card */}
            <div data-aos="fade-up" data-aos-delay="200" className="bg-white border-4 border-gray-400 shadow-brutalist p-8">
              <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-wide mb-6 text-gray-900">
                {getCategoryTitle(activeTab)}
              </h3>
              <FAQAccordion faqs={faqs} categoryFilter={activeTab} />
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section - Brutalist */}
      <section className="py-16 md:py-24 bg-indigo_dye-500 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2
            data-aos="fade-up"
            className="font-display text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-wider mb-6"
          >
            Still Have Questions?
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="100"
            className="font-body text-xl md:text-2xl text-indigo_dye-100 mb-12 max-w-2xl mx-auto leading-relaxed"
          >
            Can't find what you're looking for? Our team is here to help you with any questions about CPR training and certification.
          </p>
          <div data-aos="fade-up" data-aos-delay="200">
          <Button
            variant="outline"
            size="xl"
            onClick={() => navigate('/contact')}
            className="bg-white border-rich_black-500 text-rich_black-500 hover:bg-rich_black-500 hover:text-white"
          >
            Contact Us
          </Button>
          </div>
        </div>
      </section>
    </>
  );
}
